import { parseCostOverrideAmount } from "../../../scope-of-work/utils/parseCostOverrideAmount";

type JobWithPricing = {
  quote?: unknown;
  scopeOfWork?: { costOverride?: unknown } | null;
  quoteServiceUid?: string | null;
  depositReservedQuoteServiceUid?: string | null;
} | null;

function resolveJobQuoteServiceLineUid(job: JobWithPricing | undefined): string | null {
  const quoteServiceUid = job?.quoteServiceUid;
  if (typeof quoteServiceUid === "string" && quoteServiceUid.trim().length > 0) return quoteServiceUid;
  const depositReservedQuoteServiceUid = job?.depositReservedQuoteServiceUid;
  if (typeof depositReservedQuoteServiceUid === "string" && depositReservedQuoteServiceUid.trim().length > 0) {
    return depositReservedQuoteServiceUid;
  }
  return null;
}

/**
 * Line total derived only from the linked quote (no scope costOverride).
 */
export function getQuoteLineTotalFromJob(job: JobWithPricing | undefined): number {
  const q = job?.quote as
    | {
        services?: Array<{ total?: number | string; serviceUid?: string | null }>;
        grandServiceCostCalc?: number;
        __computed?: { totalCost?: number };
      }
    | undefined;
  if (!q) return 0;
  const lineUid = resolveJobQuoteServiceLineUid(job);
  if (Array.isArray(q.services)) {
    if (lineUid) {
      const linkedLine = q.services.find((service) => service?.serviceUid === lineUid);
      const linkedLineTotal = Number(linkedLine?.total ?? 0);
      if (Number.isFinite(linkedLineTotal) && linkedLineTotal > 0) return linkedLineTotal;
    }
    const sum = q.services.reduce((acc, s) => acc + (Number(s?.total) || 0), 0);
    if (sum > 0) return sum;
  }
  const grand = Number(q.grandServiceCostCalc ?? q.__computed?.totalCost ?? 0);
  return Number.isFinite(grand) && grand > 0 ? grand : 0;
}

/**
 * When ListItemTable / invoice context has not yet produced a positive total (e.g. timing),
 * derive a fallback from the job quote, else advanced job scope costOverride (price / value / amount).
 */
export function getFallbackInvoiceTotalFromJob(job: JobWithPricing | undefined): number {
  const fromQuote = getQuoteLineTotalFromJob(job);
  if (fromQuote > 0) return fromQuote;
  const fromOverride = parseCostOverrideAmount(job?.scopeOfWork?.costOverride);
  return fromOverride ?? 0;
}
